import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import debounce from 'lodash/debounce';

import RXService from '@Services/RxService';
import { HEATMAP_OBSERVABLE_TOPICS, HEATMAP_SPLITTER_CHANGE_STATES } from '@Pages/Heatmap/constants';
import { ScrollMatrixSubject } from '@Pages/Heatmap/HeatmapObservableSubjects';
import scrollUtils from '@Pages/Heatmap/HeatmapTable/scrollUtils';
import BaseHeader from '@Pages/Heatmap/HeatmapTable/Header/BaseHeader';

const setElementLeftOffset = (el, leftOffset) => {
  if (!el) return;
  // eslint-disable-next-line no-param-reassign
  el.style.left = `${leftOffset}px`;
};

function ColumnsHeader({ items, viewOptions, loaded, selectHeaderItem, selectedHeaderItems, hoveredItem, showStructure, handleHover }) {
  const selfRef = useRef();
  const containerRef = useRef();
  const [startPoint, setStartPoint] = useState(0);
  const [correction, setCorrection] = useState(0);
  const [headerWidth, setHeaderWidth] = useState(0);
  const [cellsToShow, setCellsToShow] = useState(0);
  const [isHSplitterDragging, setIsHSplitterDragging] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedScrollHandler = useCallback(
    debounce((scrollTop, scrollLeft) => {
      const firstCell = scrollUtils.calculateFirstCell(scrollLeft, viewOptions.cellWidth);
      const firstCellCeil = Math.ceil(firstCell);
      const cellsOnViewportHalf = Math.ceil(headerWidth / viewOptions.cellWidth / 2);
      let newCellStart = firstCellCeil - Math.ceil(cellsToShow / 2) + cellsOnViewportHalf;
      let newLeftOffset =
        scrollLeft -
        (Math.ceil(cellsToShow / 2) - firstCellCeil + firstCell - cellsOnViewportHalf) * scrollUtils.calculateCellWidth(viewOptions);

      if (newCellStart <= 0) {
        newCellStart = 0;
        newLeftOffset = 0;
      }

      setCorrection(newLeftOffset);
      setStartPoint(newCellStart);

      setElementLeftOffset(containerRef.current, -scrollLeft + newLeftOffset);
    }, viewOptions.scrollDebounce),
    [cellsToShow, viewOptions.colsAmount, viewOptions.cellWidth]
  );

  const onMatrixScrolled = useCallback(
    ({ scrollTop, scrollLeft }) => {
      setElementLeftOffset(containerRef.current, -scrollLeft + correction);
      debouncedScrollHandler(scrollTop, scrollLeft);
    },
    [correction, debouncedScrollHandler]
  );

  const terminateComponentSize = useCallback(() => {
    const _headerWidth = selfRef.current.offsetWidth;
    setHeaderWidth(_headerWidth);
    setCellsToShow(Math.ceil(_headerWidth / viewOptions.cellWidth) * viewOptions.viewBufferSize);
  }, [viewOptions.cellWidth, viewOptions.viewBufferSize]);

  const horizontalSplitterChanged = useCallback((state) => {
    if (state === HEATMAP_SPLITTER_CHANGE_STATES.moved) {
      setIsHSplitterDragging(true);
    } else if (state === HEATMAP_SPLITTER_CHANGE_STATES.released) {
      setIsHSplitterDragging(false);
    }
  }, []);

  useEffect(() => {
    terminateComponentSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const rxSubscriptions = [RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.horizontalSplitter, horizontalSplitterChanged)];
    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [horizontalSplitterChanged]);

  useEffect(() => {
    const rxSubscriptions = [ScrollMatrixSubject.subscribe({ next: onMatrixScrolled })];
    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [onMatrixScrolled]);

  return (
    <BaseHeader
      ref={selfRef}
      containerRef={containerRef}
      totalItems={viewOptions.colsAmount}
      items={items}
      loaded={loaded}
      selectHeaderItem={selectHeaderItem}
      selectedHeaderItems={selectedHeaderItems}
      hoveredItem={hoveredItem}
      showStructure={showStructure}
      handleHover={handleHover}
      isRow={false}
      startPoint={startPoint}
      cellsToShow={cellsToShow}
      style={{
        height: viewOptions.colsHeaderHeight,
        left: viewOptions.rowsHeaderWidth,
      }}
      headerClassName={classnames('heatmap__column-headers', {
        'splitter-h-dragging': isHSplitterDragging,
      })}
      headerContainerClassName="heatmap__column-headers__container"
      isVertical={viewOptions.colsHeaderHeight > viewOptions.cellWidth}
      height={viewOptions.colsHeaderHeight}
      width={viewOptions.cellWidth}
    />
  );
}

ColumnsHeader.propTypes = {
  items: PropTypes.object.isRequired,
  viewOptions: PropTypes.object.isRequired,
  loaded: PropTypes.bool.isRequired,
  selectHeaderItem: PropTypes.func,
  selectedHeaderItems: PropTypes.array.isRequired,
  hoveredItem: PropTypes.object,
  showStructure: PropTypes.bool,
  handleHover: PropTypes.func,
};

export default ColumnsHeader;
